import { parseMoney, type Cents } from './money.ts';
import { toMilli, type UnitType } from './quantity.ts';
import { normalizeCode, normalizeSearch, validateBarcode, validateProductName } from './catalog.ts';
import { DEFAULT_TAX_BPS, isValidTaxBps } from './pricing.ts';

// Importación de productos desde una planilla (Excel/CSV ya leída como filas de celdas).
// Es lógica pura: la lectura del archivo la hace la app.

export const IMPORT_FIELDS = {
  name: {
    label: 'Nombre',
    required: true,
    synonyms: ['nombre', 'producto', 'descripcion', 'articulo', 'detalle', 'nombre del producto'],
  },
  barcode: {
    label: 'Código de barras',
    required: false,
    synonyms: ['codigo de barras', 'cod barras', 'cod de barras', 'codbarras', 'barras', 'ean', 'barcode', 'upc'],
  },
  sku: {
    label: 'Código interno',
    required: false,
    synonyms: ['sku', 'codigo interno', 'codigo', 'cod', 'referencia', 'ref', 'cod interno'],
  },
  price: {
    label: 'Precio de venta',
    required: true,
    synonyms: ['precio', 'precio venta', 'precio de venta', 'pvp', 'venta', 'precio final', 'p venta', 'precio unitario'],
  },
  cost: {
    label: 'Costo',
    required: false,
    synonyms: ['costo', 'precio costo', 'precio de costo', 'costo unitario', 'compra', 'precio compra', 'precio de compra'],
  },
  category: {
    label: 'Categoría',
    required: false,
    synonyms: ['categoria', 'rubro', 'familia', 'seccion', 'grupo'],
  },
  supplier: {
    label: 'Proveedor',
    required: false,
    synonyms: ['proveedor', 'distribuidor'],
  },
  unit_type: {
    label: 'Unidad',
    required: false,
    synonyms: ['unidad', 'unidad de medida', 'medida', 'um', 'tipo de unidad'],
  },
  stock: {
    label: 'Stock actual',
    required: false,
    synonyms: ['stock', 'stock actual', 'cantidad', 'existencia', 'existencias', 'unidades'],
  },
  min_stock: {
    label: 'Stock mínimo',
    required: false,
    synonyms: ['stock minimo', 'minimo', 'stock min', 'punto de pedido'],
  },
  tax_rate: {
    label: 'IVA',
    required: false,
    synonyms: ['iva', 'alicuota', 'iva %', 'alicuota iva'],
  },
} as const;

export type ImportField = keyof typeof IMPORT_FIELDS;
export type ColumnMapping = Partial<Record<ImportField, number>>;

function normalizeHeader(value: unknown): string {
  return normalizeSearch(String(value ?? ''))
    .replace(/[^a-z0-9% ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Adivina qué columna es cada campo por el encabezado. Primero coincidencias exactas. */
export function detectColumns(headers: unknown[]): ColumnMapping {
  const normalized = headers.map(normalizeHeader);
  const mapping: ColumnMapping = {};
  const used = new Set<number>();
  const fields = Object.keys(IMPORT_FIELDS) as ImportField[];

  for (const field of fields) {
    const idx = normalized.findIndex((h, i) => !used.has(i) && (IMPORT_FIELDS[field].synonyms as readonly string[]).includes(h));
    if (idx >= 0) {
      mapping[field] = idx;
      used.add(idx);
    }
  }
  // Segunda pasada: el encabezado contiene un sinónimo largo ("Precio de venta final $").
  for (const field of fields) {
    if (mapping[field] !== undefined) continue;
    let best: { idx: number; len: number } | null = null;
    normalized.forEach((h, i) => {
      if (used.has(i)) return;
      for (const s of IMPORT_FIELDS[field].synonyms) {
        if (s.length >= 4 && h.includes(s) && (!best || s.length > best.len)) best = { idx: i, len: s.length };
      }
    });
    if (best) {
      mapping[field] = (best as { idx: number }).idx;
      used.add((best as { idx: number }).idx);
    }
  }
  return mapping;
}

export interface ImportRow {
  /** Número de fila en la planilla (la 1 es el encabezado). */
  row: number;
  name: string;
  barcode: string | null;
  sku: string | null;
  price: Cents;
  cost: Cents | null;
  category: string | null;
  supplier: string | null;
  unit_type: UnitType;
  min_stock_milli: number | null;
  /** Stock con el que arranca un producto nuevo (en productos existentes se ignora). */
  stock_milli: number | null;
  tax_bps: number;
}

export interface ImportError {
  row: number;
  message: string;
}

function text(cell: unknown): string {
  if (cell == null) return '';
  if (typeof cell === 'number') return Number.isInteger(cell) ? String(cell) : String(cell);
  return String(cell).trim();
}

function money(cell: unknown): Cents | null | 'invalid' {
  if (cell == null || text(cell) === '') return null;
  if (typeof cell === 'number') return Number.isFinite(cell) && cell >= 0 ? Math.round(cell * 100) : 'invalid';
  const parsed = parseMoney(text(cell));
  return parsed === null || parsed < 0 ? 'invalid' : parsed;
}

const UNIT_SYNONYMS: Record<string, UnitType> = {
  u: 'unit', un: 'unit', unid: 'unit', unidad: 'unit', unidades: 'unit', 'c/u': 'unit', cu: 'unit',
  kg: 'weight', kilo: 'weight', kilos: 'weight', kilogramo: 'weight', peso: 'weight', gr: 'weight',
  l: 'volume', lt: 'volume', lts: 'volume', litro: 'volume', litros: 'volume',
  m: 'length', mt: 'length', mts: 'length', metro: 'length', metros: 'length',
};

function unitType(cell: unknown): UnitType | 'invalid' {
  const v = normalizeSearch(text(cell)).replace(/\.$/, '');
  if (v === '') return 'unit';
  return UNIT_SYNONYMS[v] ?? 'invalid';
}

function quantity(cell: unknown, allowNegative = false): number | null | 'invalid' {
  if (cell == null || text(cell) === '') return null;
  const n = typeof cell === 'number' ? cell : Number(text(cell).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) && (allowNegative || n >= 0) ? toMilli(n) : 'invalid';
}

function taxBps(cell: unknown): number | 'invalid' {
  if (cell == null || text(cell) === '') return DEFAULT_TAX_BPS;
  let n = typeof cell === 'number' ? cell : Number(text(cell).replace('%', '').replace(',', '.').trim());
  if (!Number.isFinite(n)) return 'invalid';
  if (n > 0 && n < 1) n *= 100; // 0,21 → 21
  const bps = Math.round(n * 100);
  return isValidTaxBps(bps) ? bps : 'invalid';
}

function code(cell: unknown): string | null | 'scientific' {
  const raw = text(cell);
  if (raw === '') return null;
  if (/^\d+(\.\d+)?e\+\d+$/i.test(raw)) return 'scientific';
  return normalizeCode(raw);
}

/** Convierte las filas de datos (sin el encabezado) en productos válidos y errores por fila. */
export function parseImportRows(rows: unknown[][], mapping: ColumnMapping): { rows: ImportRow[]; errors: ImportError[] } {
  const result: ImportRow[] = [];
  const errors: ImportError[] = [];
  const seenBarcode = new Map<string, number>();
  const seenSku = new Map<string, number>();
  const cell = (r: unknown[], f: ImportField) => (mapping[f] === undefined ? undefined : r[mapping[f]!]);

  rows.forEach((r, i) => {
    const rowNumber = i + 2;
    if (!r || r.every((c) => text(c) === '')) return;
    const problems: string[] = [];

    const name = text(cell(r, 'name')).replace(/\s+/g, ' ');
    const nameError = validateProductName(name);
    if (nameError) problems.push(name ? nameError : 'Falta el nombre.');

    const price = money(cell(r, 'price'));
    if (price === null) problems.push('Falta el precio.');
    else if (price === 'invalid') problems.push(`Precio inválido: "${text(cell(r, 'price'))}".`);

    const cost = money(cell(r, 'cost'));
    if (cost === 'invalid') problems.push(`Costo inválido: "${text(cell(r, 'cost'))}".`);

    const barcode = code(cell(r, 'barcode'));
    if (barcode === 'scientific') {
      problems.push('El código de barras quedó en notación científica (ej. 7,79E+12). En Excel, formateá esa columna como Texto.');
    } else if (barcode) {
      const e = validateBarcode(barcode);
      if (e) problems.push(e);
      else if (seenBarcode.has(barcode)) problems.push(`Código de barras repetido (ya está en la fila ${seenBarcode.get(barcode)}).`);
    }

    const skuRaw = code(cell(r, 'sku'));
    const sku = skuRaw === 'scientific' ? null : skuRaw;
    if (sku && seenSku.has(sku)) problems.push(`Código interno repetido (ya está en la fila ${seenSku.get(sku)}).`);

    const unit = unitType(cell(r, 'unit_type'));
    if (unit === 'invalid') problems.push(`Unidad desconocida: "${text(cell(r, 'unit_type'))}" (usá u, kg, l o m).`);

    const stock = quantity(cell(r, 'stock'), true);
    if (stock === 'invalid') problems.push(`Stock inválido: "${text(cell(r, 'stock'))}".`);

    const minStock = quantity(cell(r, 'min_stock'));
    if (minStock === 'invalid') problems.push(`Stock mínimo inválido: "${text(cell(r, 'min_stock'))}".`);

    const tax = taxBps(cell(r, 'tax_rate'));
    if (tax === 'invalid') problems.push(`IVA inválido: "${text(cell(r, 'tax_rate'))}" (usá 21, 10,5, 27 o 0).`);

    if (problems.length) {
      errors.push({ row: rowNumber, message: problems.join(' ') });
      return;
    }
    if (barcode && barcode !== 'scientific') seenBarcode.set(barcode, rowNumber);
    if (sku) seenSku.set(sku, rowNumber);

    result.push({
      row: rowNumber,
      name,
      barcode: barcode && barcode !== 'scientific' ? barcode : null,
      sku,
      price: price as Cents,
      cost: cost as Cents | null,
      category: text(cell(r, 'category')) || null,
      supplier: text(cell(r, 'supplier')) || null,
      unit_type: unit as UnitType,
      min_stock_milli: minStock as number | null,
      stock_milli: stock as number | null,
      tax_bps: tax as number,
    });
  });

  return { rows: result, errors };
}

export interface ImportPlan<T> {
  creates: ImportRow[];
  updates: Array<{ existing: T; row: ImportRow }>;
}

/**
 * Separa altas de actualizaciones: una fila actualiza un producto existente si coincide
 * su código de barras o, si no tiene, su código interno. Nunca se empareja por nombre.
 */
export function planImport<T>(
  rows: ImportRow[],
  existing: { byBarcode: Map<string, T>; bySku: Map<string, T> },
): ImportPlan<T> {
  const plan: ImportPlan<T> = { creates: [], updates: [] };
  for (const row of rows) {
    const match =
      (row.barcode ? existing.byBarcode.get(row.barcode) : undefined) ??
      (row.sku ? existing.bySku.get(row.sku) : undefined);
    if (match) plan.updates.push({ existing: match, row });
    else plan.creates.push(row);
  }
  return plan;
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * Decodifica un archivo de texto. Los CSV que exporta Excel en Windows en castellano suelen
 * venir en Windows-1252 (no UTF-8): si no es UTF-8 válido, se lee como Windows-1252.
 */
export function decodeText(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^﻿/, '');
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** Separador más probable: Excel en castellano usa ";" porque la coma es el decimal. */
export function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const counts = [';', ',', '\t'].map((d) => ({ d, n: firstLine.split(d).length - 1 }));
  counts.sort((a, b) => b.n - a.n);
  return counts[0]!.n > 0 ? counts[0]!.d : ',';
}

/** CSV con comillas dobles (campos con separadores o saltos de línea, "" como comilla). */
export function parseCsv(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"' && field === '') {
      quoted = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** Planilla de ejemplo para que el comerciante complete. */
export const IMPORT_TEMPLATE_HEADERS = [
  'Nombre',
  'Código de barras',
  'Código interno',
  'Precio de venta',
  'Costo',
  'Categoría',
  'Proveedor',
  'Unidad',
  'Stock actual',
  'Stock mínimo',
  'IVA',
];
